import React, { useState, useEffect, useCallback } from 'react';
import { Button, Tooltip } from '@mui/material';
import {
  VolumeUp as PlayIcon,
  Stop as StopIcon,
  Pause as PauseIcon,
} from '@mui/icons-material';

interface ReadAloudButtonProps {
  /** CSS selector or ref for the container whose text content to read */
  contentSelector: string;
}

const ReadAloudButton: React.FC<ReadAloudButtonProps> = ({ contentSelector }) => {
  const [isReading, setIsReading] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    setSupported('speechSynthesis' in window);
  }, []);

  useEffect(() => {
    const handleEnd = () => {
      setIsReading(false);
      setIsPaused(false);
    };

    window.speechSynthesis?.addEventListener?.('end', handleEnd);
    return () => {
      window.speechSynthesis?.removeEventListener?.('end', handleEnd);
      window.speechSynthesis?.cancel();
    };
  }, []);

  const handlePlay = useCallback(() => {
    if (isPaused) {
      window.speechSynthesis.resume();
      setIsPaused(false);
      return;
    }

    const container = document.querySelector(contentSelector);
    if (!container) return;

    // Clone the container and remove elements marked with data-no-read
    const clone = container.cloneNode(true) as HTMLElement;
    clone.querySelectorAll('[data-no-read]').forEach((el) => el.remove());
    const text = clone.textContent || '';
    if (!text.trim()) return;

    window.speechSynthesis.cancel();

    // Split into chunks (speechSynthesis has a character limit in some browsers)
    const maxChunk = 3000;
    const chunks: string[] = [];
    let remaining = text;
    while (remaining.length > 0) {
      if (remaining.length <= maxChunk) {
        chunks.push(remaining);
        break;
      }
      // Find a sentence break near the limit
      let breakPoint = remaining.lastIndexOf('. ', maxChunk);
      if (breakPoint === -1 || breakPoint < maxChunk / 2) {
        breakPoint = maxChunk;
      } else {
        breakPoint += 2;
      }
      chunks.push(remaining.slice(0, breakPoint));
      remaining = remaining.slice(breakPoint);
    }

    chunks.forEach((chunk, i) => {
      const utterance = new SpeechSynthesisUtterance(chunk);
      utterance.rate = 1;
      utterance.pitch = 1;

      if (i === chunks.length - 1) {
        utterance.onend = () => {
          setIsReading(false);
          setIsPaused(false);
        };
      }

      window.speechSynthesis.speak(utterance);
    });

    setIsReading(true);
    setIsPaused(false);
  }, [contentSelector, isPaused]);

  const handlePause = useCallback(() => {
    window.speechSynthesis.pause();
    setIsPaused(true);
  }, []);

  const handleStop = useCallback(() => {
    window.speechSynthesis.cancel();
    setIsReading(false);
    setIsPaused(false);
  }, []);

  if (!supported) return null;

  if (!isReading) {
    return (
      <Tooltip title="Read aloud (accessibility)" arrow>
        <Button
          variant="outlined"
          size="small"
          startIcon={<PlayIcon />}
          onClick={handlePlay}
          sx={{ fontWeight: 600 }}
        >
          Read Aloud
        </Button>
      </Tooltip>
    );
  }

  return (
    <>
      <Tooltip title={isPaused ? 'Resume reading' : 'Pause reading'} arrow>
        <Button
          variant="outlined"
          size="small"
          startIcon={isPaused ? <PlayIcon /> : <PauseIcon />}
          onClick={isPaused ? handlePlay : handlePause}
          sx={{ fontWeight: 600, mr: 1 }}
        >
          {isPaused ? 'Resume' : 'Pause'}
        </Button>
      </Tooltip>
      <Tooltip title="Stop reading" arrow>
        <Button
          variant="outlined"
          size="small"
          color="error"
          startIcon={<StopIcon />}
          onClick={handleStop}
          sx={{ fontWeight: 600 }}
        >
          Stop
        </Button>
      </Tooltip>
    </>
  );
};

export default ReadAloudButton;
